import React, { Fragment } from "react";
import {
  Chart as ChartJS,
  registerables
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar as ChartJsBar } from 'react-chartjs-2';
import pluginLegendOnCanvases from "./pluginLegendsOnCanvases";
import shortenLabelName from "./shortenLabelName";
import replaceZentaiToBasename from "./replaceZentaiToBasename";
import { isSafari } from 'react-device-detect';

import "./Bar.css";

ChartJS.register(...registerables);

const chartPaddingLeft = 25; // y軸ラベルをテーブルの左外側に配置するためのパディング
const tickLengthBottom = 8; // y軸ラベルによる縦方向のパディングを覆うパディング

const options = {
  layout: {
    autoPadding: false, // 左右の余計なパディングを排除
    padding: {
      left: chartPaddingLeft,
    }
  },
  datasets: {
    bar: {
      borderWidth: 1,
      borderRadius: 2
    },
    line: {
      borderWidth: 0, // 折れ線を表示しない
      pointRadius: 8, // マーカーの半径
      pointBorderColor: 'white',  // マーカーの枠
      pointBorderWidth: 1,
      // clip: 10,
    }
  },
  scales: {
    yAxis: {
      min: 0,
      ticks: {
        stepSize: 20,
        mirror: true, // ラベルを反転させて余計なパディングの発生を防ぐ
        padding: 0, // 左余白の削除その1
      },
      grid: {
        drawBorder: false,  // 左端の縦グリッドを表示しない
        tickWidth: 0, // 目盛を表示しない
        tickLength: -chartPaddingLeft,  // 左方向の位置調整
      },
    },
    xAxis: {
      ticks: {
        display: false, // x軸ラベルを表示しない
      },
      grid: {
        drawBorder: false,  // 下端の横グリッドを表示しない
        drawOnChartArea: false, // 縦グリッドを表示しない
        tickWidth: 0,
        tickLength: tickLengthBottom, // 縦方向の位置調整
      },
    }
  },
  plugins: {
    legend: {
      display: false, // 凡例を表示しない
      labels: {
        // chart.options.plugins.legend.labels.generateLabels で
        // 各マーカーの情報を取得するのに必要
        usePointStyle: true,
      },
    },
    tooltip: {
      callbacks: {
        label(ctx) {
          const l = ctx.dataset.label;
          const v = ctx.dataset.data[ctx.dataIndex].toFixed(1);
          return `${l}: ${v}`;
        },
      },
    },
    datalabels: {
      formatter(v) {
        if (typeof v === 'string') {
          return v;
        } else {
          return `${v.toFixed(1)}%`;
        }
      },
      font: {
        weight: 'bold',
        size: process.env.REACT_APP_IS_EXTERNAL ? 14 : 18,
      },
      anchor(ctx) {
        return (ctx.dataset.data[ctx.dataIndex] >= 10) ? 'center' : 'end';
      },
      align(ctx) {
        return (ctx.dataset.data[ctx.dataIndex] >= 10) ? 'center' : 'end';
      },
    },
  }
};

const plugins = [
  pluginLegendOnCanvases,
  ChartDataLabels,
];

function Bar({ json, basename }) {
  const {
    labels,
    datasets,
    verticalTop = false,
    subtotalLabels = [],
    enableHighlight = true,
  } = json;

  const highlight = enableHighlight && (datasets.length >= 2);

  // データセットの作成
  const barBackGroundColor = (academyName = "") => {
    switch (academyName) {
      case "大学":
        return "#B0C2D5"
      case "短大":
        return "#F7AFC8"
      case "専門学校":
        return "#BBD0BA"
      default:
        return "#D3D3D3"
    }
  }
  const markerStyles = [
    { // 大学
      // このスタイルが設定されたデータセットは「スプレッド演算子を用いて」「他のスタイルで上書きできる」
      pointStyle: 'triangle',
      backgroundColor: '#2E75B6',
    }, {
      // 短大
      pointStyle: 'circle',
      backgroundColor: '#F06292',
    }, {
      // 専門学校
      pointStyle: 'rect',
      backgroundColor: '#45B39D',
    }
  ];
  const lineStyles = [
    {
      pointStyle: 'triangle',
      backgroundColor: '#2E75B6',
      borderColor: '#2E75B6',
    }, {
      pointStyle: 'circle',
      backgroundColor: '#F06292',
      borderColor: '#F06292',
      borderDash: [4, 2],
    }, {
      pointStyle: 'rect',
      backgroundColor: '#45B39D',
      borderColor: '#45B39D',
    }
  ];

  const academyName = () => {
    const l0 = datasets[0].label;
    if (/短大|短期大学/.test(l0)) {
      return "短大"
    } else if (/専門/.test(l0)) {
      return "専門学校"
    } else if (/大学/.test(l0)) {
      return "大学"
    }
  }

  const type2i = new Map();
  const actualDatasets = datasets.map((ds, i) => {
    const base = {
      label: ds.label,
      data: ds.data,
      order: -i,
    };
    const j = type2i.get(ds.type) ?? 0;
    type2i.set(ds.type, j + 1);
    switch (ds.type) {
      case 'bar':
        return {
          ...base,
          type: 'bar',
          backgroundColor: barBackGroundColor(academyName()),
          pointStyle: 'rect',
          barPercentage: 0.5, // 横幅の調整
          categoryPercentage: 1.0,  // 横幅の調整 (不要なパディングの削除)
        };
      case 'marker':
        return {
          ...base,
          type: 'line',
          ...markerStyles[j % markerStyles.length],
        };
      case 'line':
        return {
          ...base,
          type: 'line',
          borderWidth: 2,
          pointBorderWidth: 1,
          ...lineStyles[j % lineStyles.length],
        }
      case 'none':
        return {
          ...base,
          type: 'line',
          pointRadius: 0, // グラフにマーカーを表示しない
          pointBackgroundColor: 'rgba(0, 0, 0, 0)', // 凡例にマーカーを表示しない
        };
      default:
        console.error('不正なdataset type', ds.type);
        return base;
    }
  });

  if (datasets.length === 2 && datasets[0].type === 'bar' && datasets[1].type === 'marker') {
    const l0 = datasets[0].label;
    if (/短大|短期大学/.test(l0)) {
      actualDatasets[1] = { ...actualDatasets[1], ...markerStyles[1] };
    } else if (/専門/.test(l0)) {
      actualDatasets[1] = { ...actualDatasets[1], ...markerStyles[2] };
    }
  }

  const data = {
    labels: labels.map( l => shortenLabelName(l) ),
    datasets: actualDatasets,
  };

  // 縦棒1本だけのチャートであるかどうか
  const isSingleBar = type2i.get('bar') === 1 && (type2i.get('none') ?? 0) === datasets.length - 1;
  options.plugins.datalabels.display = isSingleBar;

  // テーブルからスコアの行をカットできるか
  const isScoreRowCut = isSingleBar && datasets.length === 1 && subtotalLabels.length === 0;

  // 描画のための補助関数
  const paddingCell = (n) => {
    if (n > 0) {
      return <td colSpan={n}></td>;
    } else {
      return null;
    }
  };
  const preSubtotalCell =
    (subtotalLabels.length > 0) ? <td className="barchart-pre-subtotal-cell"></td> : null;

  let labelHeightLen = 0; // 縦書きのときのラベルの高さを表す文字数
  if (verticalTop && !isSafari) {
    const maxLen = 20;  // 1列あたりの最大文字数
    for (const l of [...data.labels, ...subtotalLabels]) {
      for (const s of l.split('\n')) {
        labelHeightLen = Math.max(labelHeightLen, s.length);
      }
    }
    const margin = process.env.REACT_APP_IS_EXTERNAL ? 5 : 0;
    labelHeightLen = Math.min(labelHeightLen + margin, maxLen);
  }

  const label2verticalNodes = (label) => {
    const nodes = label.split('\n').map((s, i) => {
      return (
        <Fragment key={i}>
          { i > 0 && <br /> }
          {s}
        </Fragment>
      );
    });
    return <div className="barchart-vertical" style={{height: `${labelHeightLen + 0.5}em`}}>{nodes}</div>
  }

  // 凡例描画のためのCanvasへのref
  const legendCvsRefs = [];
  options.plugins[pluginLegendOnCanvases.id] = {
    canvasElementsFunction() {
      return legendCvsRefs.map((r) => r.current);
    },
  };

  // 表側 (凡例・ラベル・調査数) のセル複数個のリスト
  const sideCellsList = [];
  datasets.forEach((ds, i) => {
    const ref = React.createRef();
    legendCvsRefs.push(ref);
    const legendWidth = 40;
    const nCell =
      (ds.n === null || ds.n === undefined) ? // 調査数省略の仮実装
        ( <td className="barchart-bordered barchart-rtb-b"></td> ) :
        ( <td className="barchart-bordered barchart-right-align">
            {isScoreRowCut && <>調査数<br /></>}
            <span classes="barchart-num-font barchart-unbold">{(ds.n?.toLocaleString('ja-JP')) ?? 'undef'}</span>
          </td> );
    sideCellsList.push(
      <>
        <td className="barchart-bordered barchart-ltb-b barchart-side-cell barchart-left-align">
          <canvas ref={ref} width={legendWidth} height={legendWidth}
            style={process.env.REACT_APP_IS_EXTERNAL ? { width: "1.5em", height: "1.5em" } : { width: "1em", height: "1em" }}>
          </canvas>
          <span className={(i === 0 && highlight) ? 'barchart-bold' : ''}
            style={process.env.REACT_APP_IS_EXTERNAL && datasets.length === 1 ? { display: "block" } : { display: "inline" }}>
              {shortenLabelName(replaceZentaiToBasename(ds.label, basename))}
          </span>
        </td>
        {nCell}
      </>
    );
  });

  // 表頭ラベルのセル(小計は含まない)を生成する関数
  const labelCells = (rowSpan = 1) =>
    data.labels.map((l, i) => {
      const classes = ['barchart-bordered'];
      if (verticalTop && !isSafari) {
        classes.push('barchart-vertical-top-cell')
        l = label2verticalNodes(l);
      }
      return (
        <td key={i} className={classes.join(' ')} rowSpan={rowSpan}> {l} </td>
      );
    });

  // 図表のテーブル部分はデータセットによって分岐
  let midRows;
  if (isScoreRowCut) {
    // スコアを省略する場合
    const upperPadding = Math.max(labelHeightLen / 2, 2.5);
    midRows = (
      <>
        <tr>
          <td colSpan={2} style={{height: `${upperPadding}em`}}></td>
          {labelCells(2) /* ラベルは2行連結 */}
        </tr>
        <tr>
          {sideCellsList[0]}
        </tr>
      </>
    );
  } else {
    // スコアを省略しない場合
    const scoreRows = datasets.map((ds, i) => {
      const highlightClasses = (v, top) => {
        const classes = [];
        if (highlight) {
          if (i === 0) {
            classes.push('barchart-bold');
          } else if (v - top >= 5.0) {
            classes.push('barchart-highlight-higher');
          } else if (v - top <= -5.0) {
            classes.push('barchart-highlight-lower')
          }
        }
        return classes;
      };
      return (
        // スコアの行
        <tr key={i}>
          {sideCellsList[i]}
          {ds.data.map((v, j) => {
            // スコアの描画
            const classes = ['barchart-bordered barchart-num-font'];
            classes.push(...highlightClasses(v, datasets[0].data[j]));
            return (
              <td key={j} className={classes.join(' ')}>
                { (v === 0 || typeof v === 'string') ? "-" : v.toFixed(1) }
              </td>
            );
          })}
          {preSubtotalCell}
          {ds.subtotal ? ds.subtotal.map((v, j) => {
            // 小計の描画
            const classes = ['barchart-bordered barchart-num-font'];
            classes.push(...highlightClasses(v,datasets[0].subtotal[j]));
            return (
              <td key={j} className={classes.join(' ')}>
                { (v === 0 || typeof v === 'string') ? "-" : v.toFixed(1) }
              </td>
            );
          }) : []}
        </tr>
      );
    });
    midRows = (
      <>
        <tr>
          <td></td>
          <td className="barchart-n-top-cell">調査数</td>
          {labelCells(1)}
          {preSubtotalCell}
          {subtotalLabels.map((l, i) => {
            // 小計ラベルの描画
            const classes = ['barchart-bordered'];
            if (verticalTop && !isSafari) {
              classes.push('barchart-vertical-top-cell');
              l = label2verticalNodes(l);
            }
            return (
              <td key={i} className={classes.join(' ')}>{l}</td>
            );
          })}
        </tr>
        {scoreRows}
      </>
    );
  }

  // 仮で40remとする。クライアントの要望に応じて対応させる。
  // 仮でJSONでも設定できるようにした
  const minWidth = json.minWidth ?? 70;

  // 表側の横幅
  const sideLength = 1.5 + datasets.reduce((max, ds) => Math.max(max, replaceZentaiToBasename(ds.label).length), 0);

  // 描画
  return (
    <div className="barchart-table-container">
      <table className="barchart-chart-table" style={{minWidth: `${minWidth}rem`}}>
        <tbody>
          <tr>
            <td style={{width: `${sideLength}em`}}></td>
            <td className="barchart-second-head-cell"></td>
            <td colSpan={data.labels.length} className="barchart-canvas-cell">
              <div className="barchart-canvas-container"
                style={{
                  width: `calc(100% + ${chartPaddingLeft}px)`,
                  right: `${chartPaddingLeft}px`,
                  top: `${tickLengthBottom}px`,
                }}
              >
                <ChartJsBar
                  width="350" height="100"
                  options={options} data={data} plugins={plugins} />
              </div>
            </td>
            {preSubtotalCell}
            {paddingCell(subtotalLabels.length)}
          </tr>
          {midRows}
          <tr>
            {paddingCell(2)}
            <td colSpan={labels.length} className="barchart-left-align">
              {highlight &&
                <>
                  ※全体値より　
                  <span className="barchart-highlight-higher">　</span>：+5ポイント以上高い　
                  <span className="barchart-highlight-lower">10.0</span>：-5ポイント以上低い
                </>}
            </td>
            {preSubtotalCell}
            {paddingCell(subtotalLabels.length)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default Bar;